import { createElement, FC, useCallback, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Medicines, MedicinesProps } from './medicines';
import {
  getMedicines,
  getMedicinesFilters,
  IMedicine,
  IMedicinesFilterOptions,
} from '../../apis/medicines';

type MedicinesFilters = {
  query: string;
  page: number;
  disponibilidade: string[];
  formaFarmaceutica: string[];
  componenteAgrupado: string[];
  responsabilidade: string[];
};

type FilterKey =
  | 'disponibilidade'
  | 'formaFarmaceutica'
  | 'componenteAgrupado'
  | 'responsabilidade';

const MEDICINES_FILTERS_KEY = 'medicines-filters';
const PAGE_SIZE = 12;

const usePersistedFilter = () => {
  const location = useLocation();

  const getInitialFilters = (): MedicinesFilters => {
    const params = new URLSearchParams(location.search);

    if (typeof window !== 'undefined') {
      const stored = sessionStorage.getItem(MEDICINES_FILTERS_KEY);
      if (stored) {
        try {
          return JSON.parse(stored) as MedicinesFilters;
        } catch {}
      }
    }

    return {
      query: params.get('search') ?? params.get('q') ?? '',
      page: 1,
      disponibilidade: [],
      formaFarmaceutica: [],
      componenteAgrupado: [],
      responsabilidade: [],
    };
  };

  const [filters, setFiltersState] = useState<MedicinesFilters>(getInitialFilters);

  const setFilters = (next: MedicinesFilters) => {
    setFiltersState(next);
    if (typeof window !== 'undefined') {
      sessionStorage.setItem(MEDICINES_FILTERS_KEY, JSON.stringify(next));
    }
  };

  return [filters, setFilters] as const;
};

export const MedicinesContainer: FC = () => {
  const [filters, setFilters] = usePersistedFilter();

  const [list, setList] = useState<IMedicine[]>([]);
  const [total, setTotal] = useState<number>(0);
  const [totalPages, setTotalPages] = useState<number>(0);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  const [filterOptions, setFilterOptions] = useState<IMedicinesFilterOptions>({
    disponibilidade: [],
    formaFarmaceutica: [],
    componenteAgrupado: [],
    responsabilidade: [],
  });

  const [inputSearch, setInputSearch] = useState(filters.query);

  useEffect(() => {
    getMedicinesFilters()
      .then(setFilterOptions)
      .catch(console.error);
  }, []);

  const handleSearch = useCallback(() => {
    setIsLoading(true);
    getMedicines({
      search: filters.query || undefined,
      page: filters.page,
      limit: PAGE_SIZE,
      disponibilidade: filters.disponibilidade.length
        ? filters.disponibilidade
        : undefined,
      formaFarmaceutica: filters.formaFarmaceutica.length
        ? filters.formaFarmaceutica
        : undefined,
      componenteAgrupado: filters.componenteAgrupado.length
        ? filters.componenteAgrupado
        : undefined,
      responsabilidade: filters.responsabilidade.length
        ? filters.responsabilidade
        : undefined,
    })
      .then((res) => {
        setList(res.data);
        setTotal(res.meta.total);
        setTotalPages(res.meta.totalPages);
      })
      .catch((err) => {
        console.error(err);
        setList([]);
        setTotal(0);
        setTotalPages(0);
      })
      .finally(() => setIsLoading(false));
  }, [filters]);

  useEffect(() => {
    handleSearch();
  }, [handleSearch]);

  useEffect(() => {
    setInputSearch(filters.query);
  }, [filters.query]);

  const toggleFilter = (key: FilterKey, value: string) => {
    const current = filters[key];
    const next = current.includes(value)
      ? current.filter((v) => v !== value)
      : [...current, value];
    setFilters({ ...filters, [key]: next, page: 1 });
  };

  const clearFilterGroup = (key: FilterKey) => {
    setFilters({ ...filters, [key]: [], page: 1 });
  };

  const clearAllFilters = () => {
    setFilters({
      ...filters,
      disponibilidade: [],
      formaFarmaceutica: [],
      componenteAgrupado: [],
      responsabilidade: [],
      page: 1,
    });
  };

  const handleChangeText = useCallback(
    (text: string) => {
      setInputSearch(text);
      setFilters({ ...filters, query: text, page: 1 });
    },
    [filters, setFilters]
  );

  const handlePaginationChange = (
    _: React.ChangeEvent<unknown>,
    page: number
  ) => {
    if (typeof window !== 'undefined' && window.scrollY) window.scrollTo(0, 0);
    setFilters({ ...filters, page });
  };

  const hasActiveFilters =
    filters.disponibilidade.length +
      filters.formaFarmaceutica.length +
      filters.componenteAgrupado.length +
      filters.responsabilidade.length >
    0;

  const props: MedicinesProps = {
    list,
    isLoading,
    total,
    totalPages,
    page: filters.page,
    searchValue: inputSearch,
    onChangeSearch: handleChangeText,
    filterOptions,
    selectedDisponibilidade: filters.disponibilidade,
    selectedFormaFarmaceutica: filters.formaFarmaceutica,
    selectedComponenteAgrupado: filters.componenteAgrupado,
    selectedResponsabilidade: filters.responsabilidade,
    onToggleFilter: toggleFilter,
    onClearFilterGroup: clearFilterGroup,
    onClearAllFilters: clearAllFilters,
    hasActiveFilters,
    onPageChange: handlePaginationChange,
  };

  return createElement(Medicines, props);
};
